import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CardPayment } from '@lib/entities';
import { CardOrOneTimePayment, GiftCard } from '@lib/entities/Payment';
import { AddPaymentV4Payload } from '@lib/api/CheckoutService';
import { type ApiError } from '@lib/api/client';
import { loggedOut } from '@lib/redux/actions/lifecycle';
import { type DeferredAction, withDeferred } from '@lib/redux/deferred';

export interface WalletState {
    loading: boolean;
    /**
     * True once at least one wallet fetch has SETTLED this session. Distinguishes "not
     * fetched yet" from "fetched and empty": the payment-methods page defers its first
     * fetch until the MFA flag resolves, and `loading` is false during that window — so
     * an empty-state gated on `!loading && !cards.length` alone flashes "No Payments"
     * at users who do have cards (the JSAD-6919 loading-vs-empty class, wallet edition;
     * it also made the nightly account-settings spec misreport a healthy wallet as a
     * "vault miss" every night from 2026-08-26).
     *
     * Set by the writes walletSaga makes when a fetch settles (`setCards` on success,
     * `setWalletError` on failure) — the Zustand `fetchWallet:finishLoading` (Wallet.ts:163).
     * Refetches never reset it, so the empty state cannot re-flash on background refreshes.
     */
    hasFetched: boolean;
    cards: CardPayment[];
    selectedCard?: CardOrOneTimePayment;
    giftCards?: GiftCard[];
    jsCreditBalance: number;
    error: string | null;
    gatewayLegend?: string;
    /** True when wallet-info returns a 428 requiring device verification. */
    requireDeviceVerification?: boolean;
    /**
     * True (from wallet-info, v4+) when the user has a saved card stranded on the
     * inactive gateway with no equivalent on the active one. Drives the differentiated
     * "re-add your card" empty state (JSAD-6863).
     */
    savedCardNeedsReentry?: boolean;
}

/**
 * Request payloads. The four request actions carry an optional deferred in `meta`
 * (lib/redux/deferred.ts) so the `useWallet.redux` twin can hand its callers the Promise the
 * Zustand wallet returned (`addCard(): Promise<ApiError | undefined>`, the others
 * `Promise<void>`). Dispatchers that pass only the payload are unchanged.
 */
export interface FetchWalletPayload {
    userId?: number;
    /** When true, the first card in the results becomes the `selectedCard`. */
    selectFirstCard?: boolean;
    storeId?: number;
    /** When true, hit the v5 wallet-info endpoint instead of v4 (MFA-aware). */
    isMfaEnabled?: boolean;
}

export interface AddCardPayload {
    newCard: AddPaymentV4Payload;
    cc_last4: string;
    userId: number;
    storeId?: number;
}

export interface RemoveCardPayload {
    card: CardPayment;
    userId: number;
    storeId?: number;
}

export interface RemoveSelectedCardPayload {
    userId: number;
    storeId?: number;
}

export type FetchWalletAction = DeferredAction<FetchWalletPayload, void>;
export type AddCardAction = DeferredAction<AddCardPayload, ApiError | undefined>;
export type RemoveCardAction = DeferredAction<RemoveCardPayload, void>;
export type RemoveSelectedCardAction = DeferredAction<RemoveSelectedCardPayload, void>;

const initialState: WalletState = {
    loading: false,
    hasFetched: false,
    cards: [],
    selectedCard: undefined,
    jsCreditBalance: 0,
    error: null,
    requireDeviceVerification: false,
    savedCardNeedsReentry: false
};

const walletSlice = createSlice({
    name: 'wallet',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setCards: (state, action: PayloadAction<CardPayment[]>) => {
            state.cards = action.payload;
            state.hasFetched = true;
        },
        setSelectedCard: (state, action: PayloadAction<CardOrOneTimePayment | undefined>) => {
            state.selectedCard = action.payload;
        },
        setGiftCards: (state, action: PayloadAction<GiftCard[]>) => {
            state.giftCards = action.payload;
        },
        setJsCreditBalance: (state, action: PayloadAction<number>) => {
            state.jsCreditBalance = action.payload;
        },
        setWalletError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            // "we asked and it failed" still counts as a settled fetch (see `hasFetched`).
            if (action.payload !== null) {
                state.hasFetched = true;
            }
        },
        setGatewayLegend: (state, action: PayloadAction<string | undefined>) => {
            state.gatewayLegend = action.payload;
        },
        setRequireDeviceVerification: (state, action: PayloadAction<boolean>) => {
            state.requireDeviceVerification = action.payload;
        },
        setSavedCardNeedsReentry: (state, action: PayloadAction<boolean>) => {
            state.savedCardNeedsReentry = action.payload;
        },
        clearSelectedCard: (state) => {
            state.selectedCard = undefined;
        },
        resetWallet: (state) => {
            return initialState;
        },
        // Actions to trigger sagas (deferred in `meta`, see the payload types above)
        fetchWalletRequest: {
            reducer: (state, _action: FetchWalletAction) => {
                state.loading = true;
                state.error = null;
            },
            prepare: withDeferred<FetchWalletPayload, void>()
        },
        addCardRequest: {
            reducer: (state, _action: AddCardAction) => {
                state.loading = true;
            },
            prepare: withDeferred<AddCardPayload, ApiError | undefined>()
        },
        removeCardRequest: {
            reducer: (state, _action: RemoveCardAction) => {
                state.loading = true;
            },
            prepare: withDeferred<RemoveCardPayload, void>()
        },
        removeSelectedCardRequest: {
            reducer: (state, _action: RemoveSelectedCardAction) => {
                state.loading = true;
            },
            prepare: withDeferred<RemoveSelectedCardPayload, void>()
        }
    },
    extraReducers: (builder) => {
        // `clearLoggedInState` → `resetWallet()` (lib/hooks/useAuth.ts:120).
        builder.addCase(loggedOut, () => initialState);
    }
});

export const {
    setLoading,
    setCards,
    setSelectedCard,
    setGiftCards,
    setJsCreditBalance,
    setWalletError,
    setGatewayLegend,
    setRequireDeviceVerification,
    setSavedCardNeedsReentry,
    clearSelectedCard,
    resetWallet,
    fetchWalletRequest,
    addCardRequest,
    removeCardRequest,
    removeSelectedCardRequest
} = walletSlice.actions;

export const selectWallet = (state: { wallet: WalletState }) => state.wallet;
export const selectSelectedCard = (state: { wallet: WalletState }) => state.wallet.selectedCard;
export const selectGatewayLegend = (state: { wallet: WalletState }) => state.wallet.gatewayLegend;
export const selectSavedCardNeedsReentry = (state: { wallet: WalletState }) =>
    state.wallet.savedCardNeedsReentry;

export default walletSlice.reducer;
